import { useCallback, useEffect, useMemo, useState } from "react";
import {
  closestCenter,
  CollisionDetection,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  getFirstCollision,
  pointerWithin,
  rectIntersection,
} from "@dnd-kit/core";
import { isEqual } from "lodash-es";

import { reorderMultipleDNDLists } from "utils/dnd";
import usePrevious from "hooks/usePrevious";

import { Column, Options, Side } from "./types";
import { sliceHiddenItems } from "./utils";

export const CONTAINERS = ["left", "right"];

type UseTwoColumnDND<T extends string> = {
  onChange: (props: { left: Column<T>; right: Column<T> }) => void;
  options: Options<T>;
  leftColumn: Column<T>;
  rightColumn: Column<T>;
};

const useTwoColumnDND = <T extends string>({
  onChange,
  leftColumn,
  rightColumn,
  options,
}: UseTwoColumnDND<T>) => {
  const [activeContainerId, setActiveContainerId] = useState<null | T>(null);

  const { visible: leftVisible, hidden: leftHidden } = sliceHiddenItems(leftColumn, options);
  const { visible: rightVisible, hidden: rightHidden } = sliceHiddenItems(rightColumn, options);

  // Create initial container state from visible items
  const initialContainersState: Record<string, T[]> = useMemo(
    () => ({
      left: leftVisible.map((v) => v.value),
      right: rightVisible.map((v) => v.value),
    }),
    [leftVisible, rightVisible]
  );

  const [containersState, setContainersState] = useState(initialContainersState);
  const previousInitialContainersState = usePrevious(initialContainersState);

  // Reset container state when initial data changes
  useEffect(() => {
    const shouldReset =
      !isEqual(previousInitialContainersState, initialContainersState) &&
      !isEqual(containersState, initialContainersState) &&
      !activeContainerId;

    if (shouldReset) {
      setContainersState(initialContainersState);
    }
  }, [initialContainersState, containersState, activeContainerId, previousInitialContainersState]);

  // Utility to find which container holds an item
  const findContainer = useCallback(
    (id: T) => Object.keys(containersState).find((key) => containersState[key].includes(id)),
    [containersState]
  );

  const allVisibleItems = useMemo(
    () => [...leftVisible, ...rightVisible].map((v) => v.value),
    [leftVisible, rightVisible]
  );

  // Hide an item in a specific column
  const hideItem = useCallback(
    (side: Side, id: T) => {
      const result = { left: leftColumn, right: rightColumn };
      const targetColumn = side === "left" ? "left" : "right";

      result[targetColumn] = result[targetColumn].map((item) =>
        item.value === id ? { ...item, hidden: true } : item
      );

      onChange(result);
    },
    [leftColumn, rightColumn, onChange]
  );

  const handleDragStart = useCallback(({ active }: DragStartEvent) => {
    setActiveContainerId(active.id as T);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveContainerId(null);

      if (!event.over) return;

      const oldItems = {
        left: [...leftVisible, ...leftHidden],
        right: [...rightVisible, ...rightHidden],
      };

      const old = {
        left: leftVisible.map(({ value }) => value),
        right: rightVisible.map(({ value }) => value),
      };

      const sourceContainer = Object.keys(old).find((key) =>
        old[key as Side].includes(event.active.id as T)
      );
      const targetContainer = findContainer(event.active.id as T);

      if (!sourceContainer || !targetContainer) return;

      const sourceIndex = old[sourceContainer as Side].indexOf(event.active.id as T);
      const targetIndex = containersState[targetContainer].indexOf(event.over.id as T);

      const { left, right } = reorderMultipleDNDLists(
        oldItems,
        sourceContainer,
        targetContainer,
        sourceIndex,
        targetIndex
      );

      // Update state with non-hidden items
      const filterVisible = (column: Column<T>) =>
        column.reduce((acc, next) => (next.hidden ? acc : [...acc, next.value]), [] as T[]);

      setContainersState({
        left: filterVisible(left),
        right: filterVisible(right),
      });

      onChange({ left, right });
    },
    [leftVisible, rightVisible, onChange, containersState, findContainer, leftHidden, rightHidden]
  );

  // Reset to initial state on drag cancel
  const handleDragCancel = useCallback(() => {
    setContainersState(initialContainersState);
  }, [initialContainersState]);

  const collisionDetection: CollisionDetection = useCallback(
    (args) => {
      // Handle container sorting
      if (activeContainerId && activeContainerId in containersState) {
        return closestCenter(args);
      }

      let overId = getFirstCollision(
        pointerWithin(args).length > 0 ? pointerWithin(args) : rectIntersection(args),
        "id"
      );

      if (overId) {
        // If overId is container, find the closest item it
        if (overId in containersState && containersState[overId as T].length > 0) {
          overId = closestCenter({
            ...args,
            droppableContainers: args.droppableContainers.filter((container) =>
              containersState[overId as T].includes(container.id as T)
            ),
          })[0]?.id;
        }
        return [{ id: overId }];
      }

      return [];
    },
    [activeContainerId, containersState]
  );

  const handleDragOver = useCallback(
    ({ active, over }: DragOverEvent) => {
      const overId = over?.id;

      // Exit early if no valid target or if dragging an item container
      if (!overId || active.id in allVisibleItems) return;

      // Determine containers
      const overContainer = CONTAINERS.includes(overId as string)
        ? overId
        : findContainer(overId as T);
      const activeContainer = findContainer(active.id as T);

      // Exit if either container is not found
      if (!overContainer || !activeContainer) return;

      // Only process when moving between containers
      if (activeContainer !== overContainer) {
        setContainersState((containers) => {
          const activeItems = containers[activeContainer];
          const overItems = containers[overContainer];
          const activeItem = active.id as T;

          // Determine insertion position
          let newIndex = overItems.length;

          if (!(overId in allVisibleItems) && over) {
            const overIndex = overItems.indexOf(overId as T);

            if (overIndex >= 0) {
              const shouldBePlacedBelowOverItem =
                active.rect.current.translated &&
                over.rect &&
                active.rect.current.translated.top > over.rect.top + over.rect.height;

              newIndex = shouldBePlacedBelowOverItem ? overIndex + 1 : overIndex;
            }
          }

          // Create new state with item moved between containers
          return {
            [activeContainer]: activeItems.filter((item) => item !== activeItem),
            [overContainer]: [
              ...overItems.slice(0, newIndex),
              activeItem,
              ...overItems.slice(newIndex),
            ],
          };
        });
      }
    },
    [findContainer, allVisibleItems]
  );

  return {
    leftContainerItems: containersState.left,
    rightContainerItems: containersState.right,
    hideItem,
    handleDragStart,
    handleDragOver,
    handleDragCancel,
    handleDragEnd,
    collisionDetection,
    isDragActive: activeContainerId != null && activeContainerId in containersState,
    activeContainerId,
    isEmpty: !containersState.left.length && !containersState.right.length,
  };
};

export default useTwoColumnDND;
